import type React from 'react';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { createLottery } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookCheckbox } from '@/components/UI/HookForm/Form/Checkbox';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookRichTextEditor from '@/components/UI/HookForm/Form/RichTextEditor';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { Lottery } from '@/types/Lottery';

const emailTemplateVariables = ['{{user_name}}', '{{lottery_name}}', '{{lottery_start_date}}', '{{lottery_end_date}}'];

const CreateLotteryForm: React.FC = () => {
  const navigate = useNavigate();
  const { setValue, getValues } = useFormContext();
  const emailTemplateRef = useRef<HTMLTextAreaElement>(null);
  const smsTemplateRef = useRef<HTMLTextAreaElement>(null);
  const notSelectedEmailTemplateRef = useRef<HTMLTextAreaElement>(null);
  const notSelectedSmsTemplateRef = useRef<HTMLTextAreaElement>(null);

  const onSubmit = async (values: Omit<Lottery, 'id'>) => {
    try {
      await createLottery(values);
      navigate(buildRoute(AppRoutes.LOTTERY_LIST));
    } catch (error) {
      console.error('Failed to create lottery', error);
    }
  };

  const handleVariableClick = (variable: string, fieldName: string, ref: React.RefObject<HTMLTextAreaElement | null>) => {
    const currentValue = getValues(fieldName);
    const newCursorPosition = (ref.current?.selectionStart || 0) + variable.length;

    setValue(
      fieldName,
      [currentValue.slice(0, ref.current?.selectionStart || 0), variable, currentValue.slice(ref.current?.selectionStart || 0)].join(''),
      { shouldValidate: true, shouldDirty: true }
    );

    setTimeout(() => {
      ref.current?.setSelectionRange(newCursorPosition, newCursorPosition);
      ref.current?.focus();
    }, 0);
  };

  return (
    <Layout>
      <LayoutCard>
        <AccentTitle title='Create Lottery' />
        <div className='p-4 border-t'>
          <HookForm noDefaultClass onSubmit={onSubmit}>
            <div className='space-y-5'>
              <HookInput label='Name' name='name' id='name' type='text' />
              <div>
                <label htmlFor='description' className='block text-sm font-medium text-gray-700'>
                  Description
                </label>
                <HookRichTextEditor name='description' height='300px' />
              </div>
              <div>
                <label htmlFor='termsAndConditions' className='block text-sm font-medium text-gray-700'>
                  Terms and Conditions
                </label>
                <HookRichTextEditor name='termsAndConditions' height='300px' />
              </div>
              <HookInput label='Open Date' name='startDate' id='startDate' type='date' />
              <HookInput label='Closed Date' name='endDate' id='endDate' type='date' />
              <HookInput label='Max Participants' name='maxParticipants' id='maxParticipants' type='number' />
              <HookInput label='Total Winners' name='totalWinners' id='totalWinners' type='number' />
              <div>
                <HookTextarea label='Email Template' name='emailTemplate' id='emailTemplate' ref={emailTemplateRef} />
                <div className='mt-2 flex flex-wrap gap-2'>
                  {emailTemplateVariables.map((variable) => (
                    <button
                      type='button'
                      key={variable}
                      className='text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-mono py-1 px-2 rounded'
                      onClick={() => handleVariableClick(variable, 'emailTemplate', emailTemplateRef)}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <HookTextarea
                  label='SMS Template'
                  name='smsTemplate'
                  id='smsTemplate'
                  ref={smsTemplateRef}
                  placeholder='Enter SMS template with variables like {{user_name}}, {{lottery_name}}'
                />
                <div className='mt-2 flex flex-wrap gap-2'>
                  {emailTemplateVariables.map((variable) => (
                    <button
                      type='button'
                      key={variable}
                      className='text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-mono py-1 px-2 rounded'
                      onClick={() => handleVariableClick(variable, 'smsTemplate', smsTemplateRef)}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <HookTextarea
                  label='Not Selected Email Template'
                  name='notSelectedEmailTemplate'
                  id='notSelectedEmailTemplate'
                  ref={notSelectedEmailTemplateRef}
                  placeholder='Enter email template for not selected applicants'
                />
                <div className='mt-2 flex flex-wrap gap-2'>
                  {emailTemplateVariables.map((variable) => (
                    <button
                      type='button'
                      key={variable}
                      className='text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-mono py-1 px-2 rounded'
                      onClick={() => handleVariableClick(variable, 'notSelectedEmailTemplate', notSelectedEmailTemplateRef)}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <HookTextarea
                  label='Not Selected SMS Template'
                  name='notSelectedSmsTemplate'
                  id='notSelectedSmsTemplate'
                  ref={notSelectedSmsTemplateRef}
                  placeholder='Enter SMS template for not selected applicants'
                />
                <div className='mt-2 flex flex-wrap gap-2'>
                  {emailTemplateVariables.map((variable) => (
                    <button
                      type='button'
                      key={variable}
                      className='text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-mono py-1 px-2 rounded'
                      onClick={() => handleVariableClick(variable, 'notSelectedSmsTemplate', notSelectedSmsTemplateRef)}
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <HookTextarea
                  label='Confirmation Message'
                  name='confirmationMessage'
                  id='confirmationMessage'
                  placeholder='Enter confirmation message'
                />
              </div>
              <div>
                <HookCheckbox label='Is Active' name='isActive' id='isActive' />
              </div>
              <div>
                <HookTextarea
                  label='Inactive Validation Error Message'
                  name='inActiveValidationErrorMessage'
                  id='inActiveValidationErrorMessage'
                  placeholder='Enter validation error message when lottery is inactive'
                />
              </div>
            </div>
            <div className='mt-5'>
              <Button label='Create Lottery' btnStyle='btn-primary' type='submit' />
            </div>
          </HookForm>
        </div>
      </LayoutCard>
    </Layout>
  );
};

const defaultValues: Omit<Lottery, 'id' | 'draws'> = {
  name: '',
  description: '',
  termsAndConditions: '',
  startDate: '',
  endDate: '',
  maxParticipants: 0,
  totalWinners: 0,
  emailTemplate: '',
  smsTemplate: '',
  notSelectedEmailTemplate: '',
  notSelectedSmsTemplate: '',
  confirmationMessage: '',
  isActive: true,
  inActiveValidationErrorMessage: '',
};

const CreateLottery: React.FC = () => (
  <HookFormProvider defaultValues={defaultValues}>
    <CreateLotteryForm />
  </HookFormProvider>
);

export default CreateLottery;
